import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { DivisionMileStone, DivisionMileStones } from '../../../../api/divisionMileStones/types';
import { useSnackbarContext } from '../../../../components/globalSnackNotify/globalSnackNotifyContext';
import { useCreateMileStonesBatch, useDeleteDivisionMileStone, useUpdateMileStonesBatch } from '../../../../reactQuery/division/divisionMileStoneQuery';
import { useCreateChangeTasks, useUpdateChangeTasks } from '../../../../reactQuery/changeTasks/changeTasksQuery';
import { ChangeTask } from '../../../../api/changeTasks/types';
import { getAuth } from '../../../../redux/auth/authSlice';
import { useSelector } from 'react-redux';

interface GroupedMilestone {
     mileStoneCode: string;
     mileStoneName: string;
     mileStoneOrder: number;
     tasks: DivisionMileStone[];
}

interface UseMilestoneStateProps {
     milestones: DivisionMileStones;
     setEMilestones?: (milestones: DivisionMileStones) => void;
     initiallyExpanded?: boolean;
     onSaveDraft?: (milestones: DivisionMileStones) => void;
     onSubmitChanges?: (milestones: DivisionMileStones) => void;
     onCancel?: () => void;
     onNavigationWarning?: () => void;
}

// Optimized helper function to find corresponding task
const createTaskFinder = (allTasks: DivisionMileStone[]) => {
     const taskMap = new Map<string, DivisionMileStone>();
     allTasks.forEach(task => {
          const key = `${task.taskCode}_${task.mileStoneCode}_${task.divisionCode}_${task.mode}`;
          taskMap.set(key, task);
     });

     return (task: DivisionMileStone, targetMode: 'draft' | 'released'): DivisionMileStone | undefined => {
          const key = `${task.taskCode}_${task.mileStoneCode}_${task.divisionCode}_${targetMode}`;
          return taskMap.get(key);
     };
};

// Optimized task date getter
const getTaskDate = (task: DivisionMileStone): number => {
     return new Date(task.updatedOnDate || task.createdOnDate || 0).getTime();
};

// Optimized task grouping function
const groupTasksByCode = (tasks: DivisionMileStone[]) => {
     const groups = new Map<string, DivisionMileStone[]>();
     tasks.forEach(task => {
          const existing = groups.get(task.taskCode) || [];
          existing.push(task);
          groups.set(task.taskCode, existing);
     });
     return groups;
};

// Optimized display task selector
const selectDisplayTask = (taskGroup: DivisionMileStone[]): DivisionMileStone => {
     if (taskGroup.length === 1) return taskGroup[0];
     return taskGroup.sort((a, b) => getTaskDate(b) - getTaskDate(a))[0];
};

export const useMilestoneState = ({
     milestones,
     setEMilestones,
     initiallyExpanded = false,
     onSaveDraft,
     onSubmitChanges,
     onCancel,
     onNavigationWarning,
}: UseMilestoneStateProps) => {
     const { showSnackbar } = useSnackbarContext();
     const { userInfo } = useSelector(getAuth);
     const createBatchMutation = useCreateMileStonesBatch();
     const updateBatchMutation = useUpdateMileStonesBatch();
     const createChangeTaskMutation = useCreateChangeTasks();
     const updateChangeTaskMutation = useUpdateChangeTasks();
     const deleteMilestoneMutation = useDeleteDivisionMileStone();

     const [localMilestones, setLocalMilestones] = useState<DivisionMileStones>(milestones);
     const [checkboxState, setCheckboxState] = useState<Record<string, boolean>>({});

     const originalStateRef = useRef<{ milestones: DivisionMileStones; checkboxState: Record<string, boolean> } | null>(null);

     const [showSubmitDialog, setShowSubmitDialog] = useState(false);
     const [showNavigationDialog, setShowNavigationDialog] = useState(false);
     const [showRevertDialog, setShowRevertDialog] = useState(false);

     useEffect(() => {
          // Update local milestones when milestones prop changes
          // Use a deep comparison to avoid unnecessary resets
          const currentDataIds = new Set(localMilestones.data.map(t => t.id));
          const newDataIds = new Set(milestones.data.map(t => t.id));
          
          // Check if there are actual structural changes (tasks added/removed)
          const hasStructuralChange = 
               currentDataIds.size !== newDataIds.size ||
               Array.from(newDataIds).some(id => !currentDataIds.has(id)) ||
               Array.from(currentDataIds).some(id => !newDataIds.has(id));

          // Update local milestones
          setLocalMilestones(milestones);
          
          // Only reset checkbox state if there are structural changes
          // This preserves checkbox state when tasks are just reordered or moved
          if (hasStructuralChange) {
               setCheckboxState({});
          }

          // Store original state for potential revert
          originalStateRef.current = {
               milestones: { ...milestones },
               checkboxState: { ...checkboxState },
          };
     }, [milestones]);

     // Optimized task finder with better memoization
     const findCorrespondingTask = useMemo(() =>
          createTaskFinder(localMilestones.data),
          [localMilestones.data]
     );

     // Optimized task map with better memoization
     const taskMap = useMemo(() => {
          const map = new Map<string, DivisionMileStone>();
          localMilestones.data.forEach(task => {
               map.set(task.id, task);
          });
          return map;
     }, [localMilestones.data]);

     // Optimized grouped milestones calculation with better performance
     const groupedMilestones = useMemo(() => {
          // Group by milestone code first
          const milestoneGroups = new Map<string, GroupedMilestone>();

          localMilestones.data.forEach(milestone => {
               const existing = milestoneGroups.get(milestone.mileStoneCode);
               if (existing) {
                    existing.tasks.push(milestone);
               } else {
                    milestoneGroups.set(milestone.mileStoneCode, {
                         mileStoneCode: milestone.mileStoneCode,
                         mileStoneName: milestone.mileStoneName,
                         mileStoneOrder: milestone.mileStoneOrder,
                         tasks: [milestone],
                    });
               }
          });

          // Convert to array and sort by order
          const grouped = Array.from(milestoneGroups.values())
               .sort((a, b) => a.mileStoneOrder - b.mileStoneOrder);

          // Optimize task processing for each milestone
          grouped.forEach(milestone => {
               const taskGroups = groupTasksByCode(milestone.tasks);
               const displayTasks = Array.from(taskGroups.values())
                    .map(selectDisplayTask)
                    .sort((a, b) => a.taskOrder - b.taskOrder);

               milestone.tasks = displayTasks;
          });

          return grouped;
     }, [localMilestones.data]);

     // Optimized expanded accordions calculation
     const initialExpandedAccordions = useMemo(() => {
          if (initiallyExpanded) {
               return new Set(groupedMilestones.map(m => m.mileStoneCode));
          }
          return new Set(
               groupedMilestones
                    .filter(milestone => milestone.tasks.some(task => !task.status))
                    .map(milestone => milestone.mileStoneCode)
          );
     }, [groupedMilestones, initiallyExpanded]);

     const [expandedAccordions, setExpandedAccordions] = useState<Set<string>>(initialExpandedAccordions);


     // Optimized unsaved changes detection with better performance
     const hasUnsavedChanges = useMemo(() => {
          return localMilestones.data.some(task => {
               const checkboxValue = checkboxState[task.id];
               return checkboxValue !== undefined && checkboxValue !== task.status;
          });
     }, [localMilestones.data, checkboxState]);

     // Optimized draft changes detection with better performance
     const hasDraftChanges = useMemo(() => {
          return localMilestones.data.some(draftTask => {
               if (draftTask.mode !== 'draft') return false;

               // If there's a draft task, consider it as having draft changes
               return true;
          });
     }, [localMilestones.data]);

     // Optimized checkbox handlers with better state updates
     const handleMilestoneCheckboxChange = useCallback((milestoneCode: string, checked: boolean) => {
          const milestoneTasks = localMilestones.data.filter(item =>
               item.mileStoneCode === milestoneCode && !item.automated
          );

          setCheckboxState(prev => {
               const newState = { ...prev };
               milestoneTasks.forEach(task => {
                    newState[task.id] = checked;
               });
               return newState;
          });
     }, [localMilestones.data]);

     const handleTaskCheckboxChange = useCallback((taskId: string, checked: boolean) => {
          const currentTask = taskMap.get(taskId);
          if (!currentTask || currentTask.automated) {
               return;
          }

          setCheckboxState(prev => ({
               ...prev,
               [taskId]: checked,
          }));
     }, [taskMap]);

     const handleAccordionChange = useCallback((milestoneCode: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
          setExpandedAccordions(prev => {
               const newExpanded = new Set(prev);
               if (isExpanded) {
                    newExpanded.add(milestoneCode);
               } else {
                    newExpanded.delete(milestoneCode);
               }
               return newExpanded;
          });
     }, []);

     const handleExpandAll = useCallback(() => {
          setExpandedAccordions(new Set(groupedMilestones.map(m => m.mileStoneCode)));
     }, [groupedMilestones]);

     const handleCollapseAll = useCallback(() => {
          setExpandedAccordions(new Set());
     }, []);

     // Optimized revert function
     const handleRevertToOriginal = useCallback(() => {
          if (originalStateRef.current) {
               setLocalMilestones(originalStateRef.current.milestones);
               setCheckboxState(originalStateRef.current.checkboxState);
               showSnackbar('Changes reverted to original state', 'info');
          } else {
               setCheckboxState({});
               showSnackbar('All selections cleared', 'info');
          }

          setShowRevertDialog(false);
     }, [showSnackbar]);

     // Optimized save draft function with better error handling and performance
    const handleSaveDraft = useCallback(async () => {
          try {
               const modifiedTasks = localMilestones.data.filter(task => {
                    const checkboxValue = checkboxState[task.id];
                    return checkboxValue !== undefined && checkboxValue !== task.status;
               });

               if (modifiedTasks.length === 0) {
                    showSnackbar('No changes to save', 'info');
                    return;
               }

               const now = new Date();
               const updatedExistingDrafts: DivisionMileStone[] = [];
               const newDraftTasks: DivisionMileStone[] = [];

               modifiedTasks.forEach(task => {
                    const checkboxValue = checkboxState[task.id];
                    const existingDraft = findCorrespondingTask(task, 'draft');

                    if (existingDraft) {
                         updatedExistingDrafts.push({
                              ...existingDraft,
                              status: checkboxValue,
                              updatedOnDate: now,
                              updatedBy: userInfo.username,
                         });
                    } else {
                         newDraftTasks.push({
                              ...task,
                              id: task.id,
                              mode: 'draft' as const,
                              status: checkboxValue,
                              updatedOnDate: now,
                              updatedBy: userInfo.username,
                         });
                    }
               });

               let createdDrafts: DivisionMileStone[] = [];
               if (updatedExistingDrafts.length > 0) {
                    await updateBatchMutation.mutateAsync({
                         milestones: updatedExistingDrafts,
                         mode: 'draft',
                         workFlowId: 'default-workflow',
                    });
               }

               if (newDraftTasks.length > 0) {
                    // Capture the drafts returned from API with their actual Cosmos IDs
                    createdDrafts = await createBatchMutation.mutateAsync({
                         milestones: newDraftTasks,
                         mode: 'draft',
                         workFlowId: 'default-workflow',
                    });
               }

               const updatedMilestonesData = localMilestones.data.map(task => {
                    const updatedDraft = updatedExistingDrafts.find(draft => draft.id === task.id);
                    return updatedDraft ? updatedDraft : task;
               });

               // Use the drafts returned from API (with correct Cosmos IDs) instead of local drafts
               const mergedMilestones = updatedMilestonesData.concat(createdDrafts.length > 0 ? createdDrafts : newDraftTasks);
               const newLocalMilestones = new DivisionMileStones(
                    mergedMilestones.length.toString(),
                    mergedMilestones
               );

               setLocalMilestones(newLocalMilestones);
               if (setEMilestones) {
                    setEMilestones(newLocalMilestones);
               }

               setCheckboxState({});
               showSnackbar('Changes saved as draft successfully!', 'success');

               if (onSaveDraft) {
                    onSaveDraft(newLocalMilestones);
               }
          } catch (error) {
               showSnackbar('Failed to save draft changes', 'error');
               console.error("Error saving draft changes:", error);
          }
    }, [localMilestones, checkboxState, updateBatchMutation, createBatchMutation, showSnackbar, onSaveDraft, findCorrespondingTask, userInfo.username, setEMilestones]);

     const handleSubmitChanges = useCallback(() => {
          setShowSubmitDialog(true);
     }, []);

     /* Process already "released" tasks that were modified */
     const processReleasedTasks = useCallback(async (
          modifiedRegularTasks: DivisionMileStone[],
          changeTaskId: string
     ) => {
          if (modifiedRegularTasks.length === 0) return;

          const updatedMilestones = modifiedRegularTasks.map(task => ({
               ...task,
               status: checkboxState[task.id],
               updatedOnDate: new Date(),
               updatedBy: userInfo.username,
          }));

          await updateBatchMutation.mutateAsync({
               milestones: updatedMilestones,
               mode: "released",
               workFlowId: changeTaskId,
          });
     }, [checkboxState, updateBatchMutation, userInfo.username]);

     /* Process draft tasks and promote them to released */
     const processDraftTasks = useCallback(async (
          selectedDraftTasks: DivisionMileStone[],
          changeTaskId: string
     ) => {
          if (selectedDraftTasks.length === 0) return;

          const draftsWithExistingReleased: DivisionMileStone[] = [];
          const draftsNeedingCreate: DivisionMileStone[] = [];

          /* Separate drafts into those that already have a released record and those that don't */
          selectedDraftTasks.forEach(draft => {
               const existingReleased = findCorrespondingTask(draft, "released");
               if (existingReleased) {
                    draftsWithExistingReleased.push({
                         ...existingReleased,
                         status: draft.status,
                         updatedOnDate: new Date(),
                         updatedBy: userInfo.username,
                    });
               } else {
                    draftsNeedingCreate.push({
                         ...draft,
                         mode: "released",
                         status: checkboxState[draft.id] ?? draft.status,
                         updatedOnDate: new Date(),
                         updatedBy: userInfo.username,
                    });
               }
          });

          const runUpdatesOnTasks: Promise<any>[] = [];

          /* Prepare updates for drafts that already have a released record */
          if (draftsWithExistingReleased.length > 0) {
               runUpdatesOnTasks.push(
                    updateBatchMutation.mutateAsync({
                         milestones: draftsWithExistingReleased,
                         mode: "released",
                         workFlowId: changeTaskId,
                    })
               );
          }

          /* Prepare creations for drafts with no released record */
          if (draftsNeedingCreate.length > 0) {
               runUpdatesOnTasks.push(
                    createBatchMutation.mutateAsync({
                         milestones: draftsNeedingCreate,
                         mode: "released",
                         workFlowId: changeTaskId,
                    })
               );
          }

          /* Run create and/or update in parallel */
          if (runUpdatesOnTasks.length > 0) {
               await Promise.all(runUpdatesOnTasks);
          }

          /* Delete draft records once they have been processed */
          await Promise.all(
               selectedDraftTasks.map(draft => {
                    console.log("Deleting draft with ID:", draft.id, "divisionCode:", draft.divisionCode, "taskCode:", draft.taskCode, "mode:", draft.mode);
                    return deleteMilestoneMutation
                         .mutateAsync({ id: draft.id, divisionCode: draft.divisionCode })
                         .catch(error => {
                              console.error("Failed to delete draft milestone after promotion", {
                                   draftId: draft.id,
                                   divisionCode: draft.divisionCode,
                                   taskCode: draft.taskCode,
                                   error
                              });
                              throw error;
                         });
               })
          );
    }, [checkboxState, findCorrespondingTask, updateBatchMutation, createBatchMutation, deleteMilestoneMutation, userInfo.username]);

     const handleConfirmSubmit = useCallback(async () => {
          setShowSubmitDialog(false);

          /* Filter out the previously 'released' tasks and check if there are any changes to submit */
          const modifiedRegularTasks = localMilestones.data.filter((task) => {
               const checkboxValue = checkboxState[task.id];
               return (
                    task.mode !== "draft" &&
                    checkboxValue !== undefined &&
                    checkboxValue !== task.status
               );
          });

          /* Filter out all the tasks that are in 'draft' (basically unreleased) mode. */
          const allDraftTasks = localMilestones.data.filter((task) => task.mode === "draft");

          /* Always process all draft tasks when publishing - they will be promoted to released and then deleted */
          const selectedDraftTasks = allDraftTasks;
          
          console.log("Selected draft tasks for deletion:", selectedDraftTasks.map(d => ({
               id: d.id,
               taskCode: d.taskCode,
               mileStoneCode: d.mileStoneCode,
               divisionCode: d.divisionCode,
               mode: d.mode
          })));

          /* Create a payload for the change task to trigger the update event - clo-dvs cms */
          const createChangeTaskPayload = {
               divisionCode: localMilestones.data[0].divisionCode,
               eventName: "divisionMileStone",
               eventType: "divisionMileStonesUpdate",
               createdBy: userInfo.username,
          };

          let createChangeTaskResponse;
          try {
               createChangeTaskResponse = await createChangeTaskMutation.mutateAsync(createChangeTaskPayload as Partial<ChangeTask>);
          } catch (apiError) {
               showSnackbar("Failed to create change task", "error");
               console.error("Error creating change task:", apiError);
               return;
          }

          if (!createChangeTaskResponse || !createChangeTaskResponse.id) {
               showSnackbar("Failed to create change task", "error");
               console.error("Invalid response from createChangeTask:", createChangeTaskResponse);
               return;
          }

          const changeTaskId = createChangeTaskResponse.id;

          try {
               /* If applicable, run the updates for released tasks and draft promotions in parallel */
               await Promise.all([processReleasedTasks(modifiedRegularTasks, changeTaskId), processDraftTasks(selectedDraftTasks, changeTaskId)]);

               setCheckboxState({});
               showSnackbar("Changes submitted! Updates may take a few minutes to reflect", "success");

               if (onSubmitChanges) {
                    onSubmitChanges(localMilestones);
               }
          } catch (error) {
               if (changeTaskId) {
                    try {
                         await updateChangeTaskMutation.mutateAsync({
                              id: changeTaskId,
                              divisionCode: localMilestones.data[0].divisionCode,
                              completed: false,
                              error: "Failed to submit changes",
                              updatedBy: userInfo.username,
                         });
                    } catch (updateError) {
                         showSnackbar("Failed to update change task", "error");
                         console.error("Error updating change task:", updateError);
                    }
               }
               showSnackbar("Failed to submit changes", "error");
               console.error("Error submitting changes:", error);
          }
    }, [localMilestones, checkboxState, createChangeTaskMutation, updateChangeTaskMutation, showSnackbar, onSubmitChanges, userInfo.username, processReleasedTasks, processDraftTasks]);

     // Optimized cancel handler
     const handleCancel = useCallback(() => {
          setCheckboxState({});
          showSnackbar('Changes discarded', 'info');

          if (hasUnsavedChanges) {
               setShowRevertDialog(true);
          } else if (hasDraftChanges) {
               setShowNavigationDialog(true);
          } else {
               if (originalStateRef.current) {
                    setLocalMilestones(originalStateRef.current.milestones);
               }
               if (onCancel) {
                    onCancel();
               }
          }
    }, [hasUnsavedChanges, hasDraftChanges, onCancel, showSnackbar]);

     const handleConfirmNavigation = useCallback(() => {
          setShowNavigationDialog(false);
          setCheckboxState({});
          if (onCancel) {
               onCancel();
               showSnackbar('Changes discarded successfully.', 'info');
          }
     }, [onCancel, showSnackbar]);

     const handleCloseDialog = useCallback(() => {
          setShowSubmitDialog(false);
          setShowNavigationDialog(false);
          setShowRevertDialog(false);
     }, []);

     // Optimized utility functions with better memoization
     const getTaskCheckboxState = useCallback((taskId: string): boolean => {
          if (checkboxState[taskId] !== undefined) {
               return checkboxState[taskId];
          }

          const task = taskMap.get(taskId);
          return task ? task.status : false;
     }, [checkboxState, taskMap]);

     const getDisplayTasks = useCallback((milestone: GroupedMilestone) => {
          const groupedTasksByCode = groupTasksByCode(milestone.tasks);
          const displayTasks = Array.from(groupedTasksByCode.values())
               .map(selectDisplayTask)
               .sort((a, b) => (a.taskOrder || 0) - (b.taskOrder || 0));

          return displayTasks;
     }, []);

     const getTaskChipType = useCallback((task: DivisionMileStone) => {
          if (task.automated) {
               return { label: 'automated', color: 'default' as const };
          }

          if (task.mode === 'draft') {
               return { label: 'saved', color: 'default' as const, customColor: '#ed6c02' };
          }

          if (task.mode === 'released') {
               return { label: 'applicable', color: 'success' as const };
          }

          return { label: 'saved', color: 'default' as const, customColor: '#ed6c02' };
     }, []);

     const getMilestoneChipData = useCallback((milestone: GroupedMilestone) => {
          const displayTasks = getDisplayTasks(milestone);
          const draftCount = displayTasks.filter(task => task.mode === 'draft').length;
          const releasedCount = displayTasks.filter(task => task.mode === 'released').length;

          const savedCount = displayTasks.filter(task =>
               !task.status &&
               !task.automated &&
               task.mode !== 'draft' &&
               task.mode !== 'released'
          ).length;

          const notApplicableCount = displayTasks.filter(task => !task.status).length;

          if (draftCount > 0 || savedCount > 0 || releasedCount > 0 || notApplicableCount > 0) {
               return { draftCount, savedCount, releasedCount, notApplicableCount };
          }
          return null;
     }, [getDisplayTasks]);

    const areAllTasksChecked = useCallback((milestone: GroupedMilestone) => {
         const displayTasks = getDisplayTasks(milestone).filter(task => !task.automated);
         if (displayTasks.length === 0) {
              return false;
         }
         return displayTasks.every(task => getTaskCheckboxState(task.id));
    }, [getDisplayTasks, getTaskCheckboxState]);

    const areSomeTasksChecked = useCallback((milestone: GroupedMilestone) => {
         const displayTasks = getDisplayTasks(milestone).filter(task => !task.automated);
         return displayTasks.some(task => getTaskCheckboxState(task.id));
    }, [getDisplayTasks, getTaskCheckboxState]);

     // Check if all tasks are in released state
     const areAllTasksReleased = useMemo(() => {
          return localMilestones.data.length > 0 &&
               localMilestones.data.every(task =>
                    task.mode === 'released' || task.mode === 'release'
               );
     }, [localMilestones.data]);

     // Status chip logic hook
     const useStatusChipLogic = (
          hasDraftChanges: boolean | undefined,
          hasUnsavedChanges: boolean | undefined,
          workflowTasksCount: number | undefined,
          allTasksReleased: boolean | undefined
     ) => {
          return useMemo(() => ({
               hasChanges: Boolean(hasDraftChanges || hasUnsavedChanges)
          }), [hasDraftChanges, hasUnsavedChanges]);
     };

     // Sequence reordering handlers
     const handleReorderMilestones = useCallback(async (sourceIndex: number, destinationIndex: number) => {
          const sortedMilestones = groupedMilestones.slice();
          const [removed] = sortedMilestones.splice(sourceIndex, 1);
          sortedMilestones.splice(destinationIndex, 0, removed);

          const orderMap = new Map(sortedMilestones.map((m, i) => [m.mileStoneCode, i + 1]));

          // Update milestone orders for ALL tasks
          const updatedMilestonesData = localMilestones.data.map(task => {
               const newOrder = orderMap.get(task.mileStoneCode);
               if (newOrder !== undefined) {
                    return {
                         ...task,
                         mode: 'draft' as const,
                         mileStoneOrder: newOrder,
                         updatedOnDate: new Date(),
                         updatedBy: userInfo.username,
                    };
               }
               return task;
          });

          // Identify changes for API
          const tasksToUpdate = updatedMilestonesData.filter(updatedTask => {
               const original = localMilestones.data.find(t => t.id === updatedTask.id);
               return original && original.mileStoneOrder !== updatedTask.mileStoneOrder;
          });

          const newMilestones = new DivisionMileStones(
               updatedMilestonesData.length.toString(),
               updatedMilestonesData
          );
          setLocalMilestones(newMilestones);

          // Save sequence changes to API
          try {
               if (tasksToUpdate.length > 0) {
                    await updateBatchMutation.mutateAsync({
                         milestones: tasksToUpdate,
                         mode: 'draft',
                         workFlowId: 'milestone-reorder-workflow',
                    });
                    
                    if (setEMilestones) {
                         setEMilestones(newMilestones);
                    }
                    
                    showSnackbar('Milestone order updated successfully', 'success');
               }
          } catch (error) {
               showSnackbar('Failed to save milestone order changes', 'error');
               console.error("Error saving milestone order changes:", error);
               setLocalMilestones(localMilestones);
          }
     }, [groupedMilestones, localMilestones, updateBatchMutation, showSnackbar, userInfo.username, setEMilestones]);

     const handleReorderTasks = useCallback(async (milestoneCode: string, sourceIndex: number, destinationIndex: number) => {
          const milestone = groupedMilestones.find(m => m.mileStoneCode === milestoneCode);
          if (!milestone) return;

          const displayTasks = getDisplayTasks(milestone);
          const newDisplayOrder = [...displayTasks];
          const [removed] = newDisplayOrder.splice(sourceIndex, 1);
          newDisplayOrder.splice(destinationIndex, 0, removed);

          const orderMap = new Map(newDisplayOrder.map((t, i) => [t.taskCode, i + 1]));

          // Update task orders within the milestone
          const updatedMilestonesData = localMilestones.data.map(task => {
               if (task.mileStoneCode === milestoneCode) {
                    const newOrder = orderMap.get(task.taskCode);
                    if (newOrder !== undefined) {
                         return {
                              ...task,
                              mode: 'draft' as const,
                              taskOrder: newOrder,
                              updatedOnDate: new Date(),
                              updatedBy: userInfo.username,
                         };
                    }
               }
               return task;
          });

          // Identify changes for API
          const tasksToUpdate = updatedMilestonesData.filter(updatedTask => {
               if (updatedTask.mileStoneCode !== milestoneCode) return false;
               const original = localMilestones.data.find(t => t.id === updatedTask.id);
               return original && original.taskOrder !== updatedTask.taskOrder;
          });

          const newMilestones = new DivisionMileStones(
               updatedMilestonesData.length.toString(),
               updatedMilestonesData
          );
          setLocalMilestones(newMilestones);

          // Save sequence changes to API
          try {
               if (tasksToUpdate.length > 0) {
                    await updateBatchMutation.mutateAsync({
                         milestones: tasksToUpdate,
                         mode: 'draft',
                         workFlowId: 'task-reorder-workflow',
                    });
                    
                    if (setEMilestones) {
                         setEMilestones(newMilestones);
                    }
                    
                    showSnackbar('Task order updated successfully', 'success');
               }
          } catch (error) {
               showSnackbar('Failed to save task order changes', 'error');
               console.error("Error saving task order changes:", error);
               setLocalMilestones(localMilestones);
          }
     }, [groupedMilestones, localMilestones, updateBatchMutation, showSnackbar, getDisplayTasks, userInfo.username, setEMilestones]);

     const handleMoveTaskBetweenMilestones = useCallback(async (
          taskId: string,
          sourceMilestoneCode: string,
          destinationMilestoneCode: string,
          destinationIndex: number
     ) => {
          // Find the task being moved
          const taskToMove = taskMap.get(taskId);
          if (!taskToMove) {
               showSnackbar('Task not found', 'error');
               return;
          }

          // Find source and destination milestones
          const sourceMilestone = groupedMilestones.find(m => m.mileStoneCode === sourceMilestoneCode);
          const destinationMilestone = groupedMilestones.find(m => m.mileStoneCode === destinationMilestoneCode);

          if (!sourceMilestone || !destinationMilestone) {
               showSnackbar('Milestone not found', 'error');
               return;
          }

          // Find all versions of this task (draft and released) in the source milestone
          const tasksToMove = localMilestones.data.filter(task =>
               task.taskCode === taskToMove.taskCode &&
               task.mileStoneCode === sourceMilestoneCode &&
               task.divisionCode === taskToMove.divisionCode
          );

          // Get the representative display tasks for ordering
          const sourceDisplayTasks = getDisplayTasks(sourceMilestone)
               .filter(t => t.taskCode !== taskToMove.taskCode);
          
          const destinationDisplayTasks = getDisplayTasks(destinationMilestone);

          // Create new ordered lists of display tasks
          const newSourceDisplayOrder = [...sourceDisplayTasks];
          const newDestinationDisplayOrder = [...destinationDisplayTasks];
          newDestinationDisplayOrder.splice(destinationIndex, 0, taskToMove);

          // Create order maps
          const sourceOrderMap = new Map(newSourceDisplayOrder.map((t, i) => [t.taskCode, i + 1]));
          const destOrderMap = new Map(newDestinationDisplayOrder.map((t, i) => [t.taskCode, i + 1]));

          // Update ALL tasks in local state
          const updatedMilestonesData = localMilestones.data.map(task => {
               // 1. Check if it's the task(s) being moved
               if (task.taskCode === taskToMove.taskCode && task.mileStoneCode === sourceMilestoneCode) {
                    return {
                         ...task,
                         mode: 'draft' as const,
                         mileStoneCode: destinationMilestoneCode,
                         mileStoneName: destinationMilestone.mileStoneName,
                         taskOrder: destOrderMap.get(task.taskCode) || 1,
                         updatedOnDate: new Date(),
                         updatedBy: userInfo.username,
                    };
               }

               // 2. Check if it's in the destination milestone (for reordering)
               if (task.mileStoneCode === destinationMilestoneCode) {
                    return {
                         ...task,
                         mode: 'draft' as const,
                         taskOrder: destOrderMap.get(task.taskCode) || task.taskOrder,
                         updatedOnDate: new Date(),
                         updatedBy: userInfo.username,
                    };
               }

               // 3. Check if it's in the source milestone (for reordering)
               if (task.mileStoneCode === sourceMilestoneCode) {
                    return {
                         ...task,
                         mode: 'draft' as const,
                         taskOrder: sourceOrderMap.get(task.taskCode) || task.taskOrder,
                         updatedOnDate: new Date(),
                         updatedBy: userInfo.username,
                    };
               }

               return task;
          });

          // Identify which tasks actually changed to save to API
          const tasksToUpdate = updatedMilestonesData.filter(updatedTask => {
               const original = localMilestones.data.find(t => t.id === updatedTask.id);
               if (!original) return false;
               
               return (
                    original.taskOrder !== updatedTask.taskOrder ||
                    original.mileStoneCode !== updatedTask.mileStoneCode
               );
          });

          // Update local state
          const newMilestones = new DivisionMileStones(
               updatedMilestonesData.length.toString(),
               updatedMilestonesData
          );
          setLocalMilestones(newMilestones);

          // Save changes to API
          try {
               if (tasksToUpdate.length > 0) {
                    await updateBatchMutation.mutateAsync({
                         milestones: tasksToUpdate,
                         mode: 'draft',
                         workFlowId: 'task-move-workflow',
                    });
                    
                    if (setEMilestones) {
                         setEMilestones(newMilestones);
                    }
                    
                    showSnackbar('Task moved successfully', 'success');
               }
          } catch (error) {
               showSnackbar('Failed to move task', 'error');
               console.error("Error moving task:", error);
               setLocalMilestones(localMilestones); // Revert on error
          }
     }, [groupedMilestones, localMilestones, taskMap, updateBatchMutation, showSnackbar, userInfo.username, setEMilestones, getDisplayTasks]);

     return {
          groupedMilestones,
          expandedAccordions,
          localMilestones,
          hasDraftChanges,
          hasUnsavedChanges,
          areAllTasksReleased,
          showSubmitDialog,
          showNavigationDialog,
          showRevertDialog,
          handleMilestoneCheckboxChange,
          handleTaskCheckboxChange,
          getTaskCheckboxState,
          getDisplayTasks,
          getTaskChipType,
          getMilestoneChipData,
          areAllTasksChecked,
          areSomeTasksChecked,
          handleAccordionChange,
          handleExpandAll,
          handleCollapseAll,
          handleSaveDraft,
          handleSubmitChanges,
          handleConfirmSubmit,
          handleCancel,
          handleConfirmNavigation,
          handleRevertToOriginal,
          handleCloseDialog,
          useStatusChipLogic,
          handleReorderMilestones,
          handleReorderTasks,
          handleMoveTaskBetweenMilestones,
     };
}; 